import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { SplitLines } from "@/components/motion/Reveal";
import { EngineInterface } from "@/components/home/EngineInterface";
import { useBrokers } from "@/context/BrokersContext";

const ease = [0.2, 0.8, 0.2, 1];

/** Pin the hero only on large viewports and when motion is welcome. */
const useCinematic = () => {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const wide = window.matchMedia("(min-width: 1024px)");
    const calm = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setOn(wide.matches && !calm.matches);
    sync();
    wide.addEventListener("change", sync);
    calm.addEventListener("change", sync);
    return () => { wide.removeEventListener("change", sync); calm.removeEventListener("change", sync); };
  }, []);
  return on;
};

export const Hero = () => {
  const { brokers } = useBrokers();
  const STATS = [
    [String(brokers.length), "Brokers verified"],
    ["9", "Instruments tracked"],
    ["22:00", "Daily swap refresh · UTC"],
  ];
  const wrap = useRef(null);
  const cinematic = useCinematic();
  const [vh, setVh] = useState(900);

  useEffect(() => {
    const sync = () => setVh(window.innerHeight);
    sync();
    window.addEventListener("resize", sync);
    return () => window.removeEventListener("resize", sync);
  }, []);

  const { scrollYProgress } = useScroll({ target: wrap, offset: ["start start", "end end"] });

  // Headline composition drifts up and dissolves past the top of the viewport.
  const headY = useTransform(scrollYProgress, [0, 0.72], [0, -vh * 0.7]);
  const headOpacity = useTransform(scrollYProgress, [0, 0.16, 0.46], [1, 1, 0]);
  // The engine rises into prominence as the headline clears.
  const uiY = useTransform(scrollYProgress, [0, 0.92], [0, -vh * 0.49]);
  const uiScale = useTransform(scrollYProgress, [0, 0.92], [0.93, 1]);
  const uiOpacity = useTransform(scrollYProgress, [0, 0.22], [0.82, 1]);

  const headStyle = cinematic ? { y: headY, opacity: headOpacity } : undefined;
  const uiStyle = cinematic ? { top: "64vh", y: uiY, scale: uiScale, opacity: uiOpacity, transformOrigin: "50% 0%" } : undefined;

  return (
    <section
      ref={wrap}
      className={`relative grain ${cinematic ? "-mt-[72px]" : ""}`}
      style={cinematic ? { height: "230vh" } : undefined}
      data-testid="hero-section"
    >
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,_rgba(0,82,255,0.08),_transparent_55%),radial-gradient(ellipse_at_bottom_right,_rgba(148,163,184,0.18),_transparent_55%)]" />
        <div className="absolute inset-0 dots opacity-70 [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]" />
      </div>

      <div className={cinematic ? "sticky top-0 h-screen overflow-hidden" : ""}>
        {/* ——— headline composition ——— */}
        <motion.div
          style={headStyle}
          className={cinematic ? "container-x absolute inset-x-0 top-0 pt-[12vh] text-center will-change-transform" : "container-x pt-16 pb-10 text-center"}
          data-testid="hero-headline-block"
        >
          <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease, delay: 0.1 }} className="eyebrow mb-7 text-[var(--cobalt)]" data-testid="hero-eyebrow">
            A smarter way to trade
          </motion.p>
          <SplitLines
            lines={["Find the right broker.", <span key="l2" className="text-[var(--ink-3)]">Trade with <span className="text-[var(--cobalt)]">confidence.</span></span>]}
            className="t-hero mx-auto max-w-[16ch] lg:max-w-none"
            delay={0.25}
          />
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease, delay: 0.7 }} className="mx-auto mt-7 max-w-[560px] t-lead">
            Compare brokers, discover trading advantages and make more informed decisions — with transparent scores that are never influenced by payouts.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease, delay: 0.85 }} className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <Link to="/brokers/finder" className="btn-primary" data-testid="hero-find-broker-cta">Find my broker <ArrowRight size={16} /></Link>
            <Link to="/swaps" className="btn-ghost" data-testid="hero-swaps-link">Explore swap rates</Link>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.2 }} className="mt-10 flex flex-wrap items-start justify-center gap-x-10 gap-y-5" data-testid="hero-stats">
            {STATS.map(([v, l]) => (
              <div key={l} className="text-center">
                <p className="t-stat-sm">{v}</p>
                <p className="t-stat-label">{l}</p>
              </div>
            ))}
          </motion.div>
        </motion.div>

        {/* ——— broker engine preview ——— */}
        <motion.div
          style={uiStyle}
          className={cinematic ? "container-x absolute inset-x-0 h-[74vh] will-change-transform" : "container-x pb-16"}
        >
          <motion.div
            className="h-full"
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, ease, delay: 0.55 }}
          >
            <EngineInterface />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
